import React, { useState } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { Summer2026Provider, useSummer2026 } from './context/Summer2026Context';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import Summer2026LoginPage from './pages/Summer2026LoginPage';
import Summer2026Dashboard from './pages/Summer2026Dashboard';
import { Toaster } from './components/ui/sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft, Tablet, Bus } from 'lucide-react';
import { Button } from './components/ui/button';
import '@fontsource/fredoka/400.css';
import '@fontsource/fredoka/500.css';
import '@fontsource/fredoka/600.css';
import '@fontsource/fredoka/700.css';
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/500.css';
import '@fontsource/nunito/600.css';
import '@fontsource/nunito/700.css';
import './App.css';
import Summer2026FrontDesk from './components/Summer2026FrontDesk';
import BusAttendanceViewer from './components/BusAttendanceViewer';

function HolidayCampApp({ onSwitchMode }) {
  const { isAuthenticated } = useApp();
  if (!isAuthenticated) return <LoginPage onSwitchMode={onSwitchMode} />;
  return <DashboardPage onSwitchMode={onSwitchMode} />;
}

function Summer2026App({ onSwitchMode }) {
  const { isAuthenticated, loggedInGroup, logout } = useSummer2026();
  if (!isAuthenticated) return <Summer2026LoginPage onSwitchMode={onSwitchMode} />;
  if (loggedInGroup === 'frontdesk') return <Summer2026FrontDesk onLogout={logout} />;
  return <Summer2026Dashboard onSwitchMode={onSwitchMode} />;
}

function FrontDeskApp({ onSwitchMode }) {
  const { isAuthenticated, loggedInGroup, login, logout, syncRoster, roster, loading } = useSummer2026();
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');

  // Always sync roster when entering front desk mode
  React.useEffect(() => {
    syncRoster();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // If authenticated as frontdesk, show front desk view
  if (isAuthenticated && loggedInGroup === 'frontdesk') {
    return <Summer2026FrontDesk onLogout={() => { logout(); onSwitchMode(); }} />;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password.trim()) { setError('Please enter the password'); return; }
    const result = login('frontdesk', password);
    if (!result.success) { setError('Incorrect password'); setPassword(''); }
  };

  const logoUrl = 'https://customer-assets.emergentagent.com/job_camp-attendance-app/artifacts/lojn686w_Riverview_Athletics_Logo.png';

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <div className="bg-emerald-700 py-8 px-4 relative">
        <button
          onClick={onSwitchMode}
          className="absolute top-4 left-4 text-white/80 hover:text-white flex items-center gap-1 text-sm"
          data-testid="frontdesk-back-to-programs"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <img src={logoUrl} alt="Rolling River Day Camp" className="w-32 h-32 rounded-full bg-white p-1 shadow-lg" />
          <h1 className="mt-4 text-2xl font-bold text-white font-['Fredoka']">Front Desk</h1>
          <p className="text-white/80 text-sm mt-1">Late Arrivals & Early Dismissals</p>
        </motion.div>
      </div>

      <motion.div
        className="flex-1 flex items-center justify-center px-6 py-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-[#E2E8F0]">
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <Tablet className="w-6 h-6 text-emerald-700" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-center text-[#0F172A] font-['Fredoka'] mb-2">Front Desk Login</h2>
            <p className="text-sm text-center text-[#475569] mb-6">Enter password to access the sign-in tablet</p>

            <form onSubmit={handleSubmit}>
              <div className="relative mb-4">
                <label className="block text-sm font-medium text-[#475569] mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    data-testid="frontdesk-password-input"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    className="w-full h-14 rounded-xl border border-[#E2E8F0] bg-white text-black placeholder:text-gray-400 pr-12 pl-4 text-lg font-medium focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all"
                    autoComplete="off"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569] transition-colors"
                  >
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <motion.p
                  className="text-red-500 text-sm text-center mb-4"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="frontdesk-login-error"
                >
                  {error}
                </motion.p>
              )}

              <Button
                type="submit"
                data-testid="frontdesk-login-submit"
                className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 active:translate-y-0"
              >
                Open Front Desk
              </Button>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function BusAttendanceApp({ onSwitchMode }) {
  const { isAuthenticated, login, logout, syncRoster, roster } = useSummer2026();
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);

  React.useEffect(() => {
    if (roster.length === 0) syncRoster();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loggedIn || (isAuthenticated)) {
    return <BusAttendanceViewer date={null} onClose={() => { logout(); setLoggedIn(false); onSwitchMode(); }} />;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password.trim()) { setError('Please enter the password'); return; }
    if (password.toLowerCase().trim() === 'camp1993') {
      login('admin', password);
      setLoggedIn(true);
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };

  const logoUrl = 'https://customer-assets.emergentagent.com/job_camp-attendance-app/artifacts/lojn686w_Riverview_Athletics_Logo.png';

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <div className="bg-[#0055A4] py-8 px-4 relative">
        <button
          onClick={onSwitchMode}
          className="absolute top-4 left-4 text-white/80 hover:text-white flex items-center gap-1 text-sm"
          data-testid="bus-back-to-programs"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <img src={logoUrl} alt="Rolling River Day Camp" className="w-32 h-32 rounded-full bg-white p-1 shadow-lg" />
          <h1 className="mt-4 text-2xl font-bold text-white font-['Fredoka']">Bus Attendance</h1>
          <p className="text-white/80 text-sm mt-1">Morning bus check-in status</p>
        </motion.div>
      </div>

      <motion.div
        className="flex-1 flex items-center justify-center px-6 py-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-[#E2E8F0]">
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Bus className="w-6 h-6 text-blue-700" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-center text-[#0F172A] font-['Fredoka'] mb-2">Bus Attendance Login</h2>
            <p className="text-sm text-center text-[#475569] mb-6">Enter password to view bus attendance</p>

            <form onSubmit={handleSubmit}>
              <div className="relative mb-4">
                <label className="block text-sm font-medium text-[#475569] mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    data-testid="bus-password-input"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    className="w-full h-14 rounded-xl border border-[#E2E8F0] bg-white text-black placeholder:text-gray-400 pr-12 pl-4 text-lg font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                    autoComplete="off"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569] transition-colors"
                  >
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <motion.p
                  className="text-red-500 text-sm text-center mb-4"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="bus-login-error"
                >
                  {error}
                </motion.p>
              )}

              <Button
                type="submit"
                data-testid="bus-login-submit"
                className="w-full h-14 bg-[#0055A4] hover:bg-[#004080] text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 active:translate-y-0"
              >
                View Bus Attendance
              </Button>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function ModeSelectorScreen({ onSelect }) {
  const logoUrl = 'https://customer-assets.emergentagent.com/job_camp-attendance-app/artifacts/lojn686w_Riverview_Athletics_Logo.png';

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <div className="bg-[#0055A4] py-8 px-4">
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <img
            src={logoUrl}
            alt="Rolling River Day Camp"
            className="w-28 h-28 rounded-full bg-white p-1 shadow-lg"
          />
          <h1 className="mt-4 text-2xl font-bold text-white font-['Fredoka']">
            RRDC Attendance
          </h1>
          <p className="text-white/80 text-sm mt-1">Rolling River Day Camp</p>
        </motion.div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm space-y-4">
          <motion.h2
            className="text-xl font-bold text-center text-[#0F172A] font-['Fredoka'] mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Select Program
          </motion.h2>

          <motion.button
            onClick={() => onSelect('holiday')}
            className="w-full bg-white rounded-2xl shadow-lg border-2 border-[#E2E8F0] hover:border-[#0055A4] p-6 text-left transition-all hover:shadow-xl group"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            data-testid="mode-holiday-camp"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7 text-amber-600"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] group-hover:text-[#0055A4] transition-colors">
                  Holiday Camp
                </h3>
                <p className="text-sm text-[#475569] mt-1">Spring 2026 - Google Sheets</p>
              </div>
            </div>
          </motion.button>

          <motion.button
            onClick={() => onSelect('summer2026')}
            className="w-full bg-white rounded-2xl shadow-lg border-2 border-[#E2E8F0] hover:border-[#0055A4] p-6 text-left transition-all hover:shadow-xl group"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            data-testid="mode-summer-2026"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7 text-blue-600"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] group-hover:text-[#0055A4] transition-colors">
                  Summer 2026
                </h3>
                <p className="text-sm text-[#475569] mt-1">Jun 29 - Aug 20 - CampMinder</p>
              </div>
            </div>
          </motion.button>

          <motion.button
            onClick={() => onSelect('frontdesk')}
            className="w-full bg-white rounded-2xl shadow-lg border-2 border-[#E2E8F0] hover:border-emerald-500 p-6 text-left transition-all hover:shadow-xl group"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            data-testid="mode-front-desk"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7 text-emerald-600"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] group-hover:text-emerald-600 transition-colors">
                  Front Desk
                </h3>
                <p className="text-sm text-[#475569] mt-1">Tablet sign-in for late arrivals & early dismissals</p>
              </div>
            </div>
          </motion.button>

          <motion.button
            onClick={() => onSelect('busattendance')}
            className="w-full bg-white rounded-2xl shadow-lg border-2 border-[#E2E8F0] hover:border-[#0055A4] p-6 text-left transition-all hover:shadow-xl group"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            data-testid="mode-bus-attendance"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Bus className="w-7 h-7 text-sky-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] group-hover:text-[#0055A4] transition-colors">
                  Bus Attendance
                </h3>
                <p className="text-sm text-[#475569] mt-1">Morning bus check-in from rrdcbusmapping.com</p>
              </div>
            </div>
          </motion.button>
        </div>
      </div>

      <div className="pb-4 flex items-center justify-center gap-2">
        <span className="text-[10px] text-[#94A3B8]" data-testid="app-version">v8.3</span>
      </div>
    </div>
  );
}

function App() {
  const [mode, setMode] = useState(() => localStorage.getItem('app_mode') || null);

  const handleSelectMode = (m) => {
    setMode(m);
    localStorage.setItem('app_mode', m);
  };

  const handleSwitchMode = () => {
    setMode(null);
    localStorage.removeItem('app_mode');
  };

  return (
    <AppProvider>
      <Summer2026Provider>
        <div className="App">
          <AnimatePresence mode="wait">
            {!mode && (
              <motion.div key="selector" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <ModeSelectorScreen onSelect={handleSelectMode} />
              </motion.div>
            )}
            {mode === 'holiday' && (
              <motion.div key="holiday" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <HolidayCampApp onSwitchMode={handleSwitchMode} />
              </motion.div>
            )}
            {mode === 'summer2026' && (
              <motion.div key="summer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Summer2026App onSwitchMode={handleSwitchMode} />
              </motion.div>
            )}
            {mode === 'frontdesk' && (
              <motion.div key="frontdesk" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <FrontDeskApp onSwitchMode={handleSwitchMode} />
              </motion.div>
            )}
            {mode === 'busattendance' && (
              <motion.div key="busattendance" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <BusAttendanceApp onSwitchMode={handleSwitchMode} />
              </motion.div>
            )}
          </AnimatePresence>
          <Toaster position="top-center" />
        </div>
      </Summer2026Provider>
    </AppProvider>
  );
}

export default App;
